<template>
  <div
    ref="messages_container"
    class="flex-1 space-y-4 overflow-y-auto p-4"
    @click="handle_container_click"
  >
    <div
      v-if="messages.length === 0"
      class="flex h-full flex-col items-center justify-center gap-3 text-center"
    >
      <img
        :src="ziggy_laptop"
        alt="Ziggy"
        class="h-24 w-24 object-contain"
      />
      <div>
        <p class="text-sm font-medium text-surface-700">{{ $t('ai.assistant.empty_state.title') }}</p>
        <p class="mt-1 text-xs text-surface-400">{{ $t('ai.assistant.empty_state.subtitle') }}</p>
      </div>
      <div class="mt-2 flex flex-wrap justify-center gap-2">
        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          class="rounded-full border border-surface-200 px-3 py-1.5 text-xs text-surface-600 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
          @click="$emit('send_suggestion', suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>
    </div>

    <template v-else>
      <div
        v-for="(message, index) in messages"
        :key="index"
        ref="message_elements"
      >
        <div :class="['flex gap-2', message.attributes.role === 'user' ? 'justify-end' : 'justify-start']">
          <img
            v-if="message.attributes.role === 'assistant'"
            :src="ziggy_laptop"
            alt="Ziggy"
            class="mt-1 h-6 w-6 flex-shrink-0 rounded-full bg-primary-50 object-contain"
          />
          <div
            :class="[
              'rounded-2xl px-3 py-2 text-sm',
              message.attributes.role === 'user'
                ? 'max-w-[280px] rounded-tr-sm bg-primary-500 text-white'
                : 'w-full rounded-tl-sm bg-surface-100 text-surface-800',
            ]"
          >
            <p
              v-if="message.attributes.role === 'user'"
              class="leading-relaxed whitespace-pre-wrap"
            >
              {{ message.attributes.content }}
            </p>
            <div
              v-else
              class="markdown leading-relaxed"
              v-html="render_markdown(message.attributes.content)"
            />
          </div>
        </div>
        <div
          v-if="support_cta_message_id === message.id"
          class="mt-2 ml-8 flex items-center gap-2"
        >
          <span class="h-px flex-1 bg-gradient-to-r from-transparent to-surface-200" />
          <span class="text-xs text-surface-500">{{ $t('ai.assistant.support_cta.prompt') }}</span>
          <button
            class="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition hover:border-primary-300 hover:bg-primary-100"
            @click="open_support_chat(message)"
          >
            <FontAwesomeIcon :icon="['far', 'comment']" />
            {{ $t('ai.assistant.support_cta.action') }}
          </button>
          <span class="h-px flex-1 bg-gradient-to-l from-transparent to-surface-200" />
        </div>
      </div>

      <div
        v-if="is_loading"
        class="flex items-center gap-2"
      >
        <img
          :src="ziggy_laptop"
          alt="Ziggy"
          class="mt-1 h-6 w-6 flex-shrink-0 rounded-full bg-primary-50 object-contain"
        />
        <div class="flex items-center gap-1 rounded-2xl rounded-tl-sm bg-surface-100 px-3 py-2.5">
          <span
            v-for="i in 3"
            :key="i"
            class="h-1.5 w-1.5 animate-bounce rounded-full bg-surface-400"
            :style="{ animationDelay: `${(i - 1) * 150}ms` }"
          />
        </div>
      </div>

      <div
        v-if="cap_reached"
        class="flex justify-center pt-1"
      >
        <div class="rounded-2xl border border-surface-200 bg-surface-50 px-4 py-3 text-center">
          <p class="text-xs text-surface-500">{{ $t('ai.assistant.cap.message') }}</p>
          <button
            class="mt-1.5 text-xs font-medium text-primary-600 underline-offset-2 hover:text-primary-700 hover:underline"
            @click="$emit('new_conversation')"
          >
            {{ $t('ai.assistant.cap.action') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const props = defineProps({
  messages: { type: Array, required: true },
  is_loading: { type: Boolean, required: true },
  suggestions: { type: Array, required: true },
  cap_reached: { type: Boolean, required: true },
  ziggy_laptop: { type: String, required: true },
});

const emit = defineEmits(['send_suggestion', 'new_conversation', 'close_assistant']);

const router = useRouter();
const messages_container = ref(null);
const message_elements = ref([]);

const handle_container_click = (event) => {
  const anchor = event.target.closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || anchor.getAttribute('target') === '_blank') return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  let url;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (!ZIGGU_HOST_PATTERN.test(url.hostname)) return;

  event.preventDefault();
  router.push(url.pathname + url.search + url.hash);
};

const ZIGGU_HOST_PATTERN = /^ziggu\.(loc|dev|me|app)$/;

DOMPurify.removeAllHooks();
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') return;
  const href = node.getAttribute('href');
  if (!href) return;

  let is_external = true;
  try {
    is_external = !ZIGGU_HOST_PATTERN.test(new URL(href, window.location.href).hostname);
  } catch {
    is_external = false;
  }
  if (is_external) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  } else {
    node.removeAttribute('target');
    node.removeAttribute('rel');
  }
});

const render_markdown = (content) => DOMPurify.sanitize(marked.parse(content));

const support_cta_message_id = computed(() => {
  for (let i = props.messages.length - 1; i >= 0; i -= 1) {
    const m = props.messages[i];
    if (m.attributes.role === 'assistant' && m.attributes.offer_support_cta) return m.id;
  }
  return null;
});

const open_support_chat = (message) => {
  if (!window.Intercom) return;

  const user_msg = [...props.messages]
    .slice(0, props.messages.indexOf(message))
    .reverse()
    .find((m) => m.attributes.role === 'user');

  const user_question = user_msg && user_msg.attributes.content ? user_msg.attributes.content : '';

  window.Intercom('update', {
    last_ai_question: _.truncate(user_question, { length: 250 }),
    last_ai_answer: _.truncate(message.attributes.content, { length: 250 }),
    last_ai_escalated_at: new Date().toISOString(),
    last_ai_chat_id: message.attributes.chat_id,
  });
  window.Intercom('showNewMessage', '');
  emit('close_assistant');
};

const scroll_to_latest = () => {
  const last = message_elements.value[message_elements.value.length - 1];
  if (last) last.scrollIntoView({ block: 'start', behavior: 'smooth' });
};

watch(
  () => props.messages.length,
  () => nextTick(scroll_to_latest),
);
watch(
  () => props.is_loading,
  () => nextTick(scroll_to_latest),
);
</script>

<style scoped>
.markdown :deep(p) {
  margin-bottom: 0.5rem;
}
.markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.markdown :deep(ul),
.markdown :deep(ol) {
  margin: 0.25rem 0 0.5rem 1rem;
}
.markdown :deep(ul) {
  list-style-type: disc;
}
.markdown :deep(ol) {
  list-style-type: decimal;
}
.markdown :deep(li) {
  margin-bottom: 0.15rem;
}
.markdown :deep(strong) {
  font-weight: 600;
}
.markdown :deep(a) {
  color: var(--p-primary-600);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.markdown :deep(a:hover) {
  color: var(--p-primary-700);
}
.markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 0.75rem;
  display: block;
  overflow-x: auto;
}
.markdown :deep(th) {
  background-color: var(--p-surface-200);
  padding: 0.35rem 0.6rem;
  text-align: left;
  font-weight: 600;
  border: 1px solid var(--p-surface-300);
}
.markdown :deep(td) {
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--p-surface-200);
  vertical-align: top;
}
.markdown :deep(tr:nth-child(even) td) {
  background-color: var(--p-surface-50);
}
</style>
